import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FIELDS = [
  ["po_number", "PO Number *"], ["supplier_name", "Supplier *"], ["supplier_email", "Supplier Email"],
  ["supplier_phone", "Supplier Phone"], ["po_date", "PO Date"], ["item", "Item"], ["quantity", "Quantity"],
  ["value", "Value"], ["required_delivery_date", "Required Delivery Date"], ["delivery_location", "Delivery Location"], ["buyer", "Buyer"],
];

export default function ImportPO() {
  const [step, setStep] = useState(1);
  const [parsed, setParsed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(r.data);
      setStep(2);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const remap = (field, column) => {
    const mapping = { ...parsed.mapping, [field]: column === "__none" ? undefined : column };
    const rows = parsed.rows.map((r) => {
      const data = {};
      FIELDS.forEach(([f]) => {
        const col = mapping[f];
        data[f] = col ? String(parsed.rawRows?.[r.i]?.[col] ?? r.data[f] ?? "") : (f === "po_number" || f === "supplier_name" ? "" : r.data[f] || "");
      });
      const errors = [];
      if (!data.po_number) errors.push("Missing PO number");
      if (!data.supplier_name) errors.push("Missing supplier");
      return { ...r, data, errors };
    });
    setParsed({ ...parsed, mapping, rows });
  };

  const confirm = async () => {
    setLoading(true);
    try {
      const valid = parsed.rows.filter((r) => !r.errors.length);
      const r = await api.post("/import/confirm", { rows: valid });
      setResult({ ...r.data, rejected: parsed.rows.length - valid.length });
      setStep(3);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const validCount = parsed?.rows.filter((r) => !r.errors.length).length ?? 0;

  return (
    <div data-testid="import-page" className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Import Purchase Orders</h1>
        <p className="text-sm text-muted-foreground">Bulk upload POs from CSV or Excel. Columns are auto-mapped and validated before import.</p>
      </div>

      <div className="flex items-center gap-0" data-testid="import-stepper">
        {["Upload File", "Map & Validate", "Import Result"].map((label, i) => (
          <div key={label} className="flex items-center">
            <div className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium ${step === i + 1 ? "border-slate-900 bg-slate-900 text-white" : step > i + 1 ? "border-green-300 bg-green-50 text-green-800" : "bg-white text-slate-500"}`}>
              <span className="font-mono text-xs">{i + 1}</span> {label}
            </div>
            {i < 2 && <div className="h-px w-10 bg-slate-300" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <label data-testid="upload-dropzone" className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed bg-card p-16 text-center transition-colors hover:border-slate-400 hover:bg-slate-50">
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="upload-input" onChange={(e) => upload(e.target.files[0])} />
          {loading ? <Loader2 className="h-10 w-10 animate-spin text-slate-400" /> : <UploadCloud className="h-10 w-10 text-slate-400" />}
          <p className="mt-4 font-heading text-lg font-semibold">Drop your PO file here, or click to browse</p>
          <p className="mt-1 text-sm text-muted-foreground">.csv, .xlsx — columns like PO Number, Supplier, Item, Quantity, Value, Required Delivery Date…</p>
        </label>
      )}

      {step === 2 && parsed && (
        <div className="space-y-4">
          <div className="rounded-lg border bg-card">
            <h2 className="flex items-center gap-2 border-b px-5 py-3 font-heading text-sm font-semibold"><FileSpreadsheet className="h-4 w-4" /> Column Mapping</h2>
            <div className="grid grid-cols-2 gap-4 p-5 md:grid-cols-3 lg:grid-cols-4">
              {FIELDS.map(([f, label]) => (
                <div key={f}>
                  <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
                  <Select value={parsed.mapping[f] || "__none"} onValueChange={(v) => remap(f, v)}>
                    <SelectTrigger data-testid={`map-${f}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="__none">— not mapped —</SelectItem>
                      {parsed.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border bg-card">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h2 className="font-heading text-sm font-semibold">Validation Preview ({parsed.rows.length} rows)</h2>
              <p className="text-sm"><span className="font-semibold text-[#166534]">{validCount} valid</span> · <span className="font-semibold text-[#DC2626]">{parsed.rows.length - validCount} with errors</span></p>
            </div>
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-xs" data-testid="import-preview-table">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b text-left uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2">#</th>
                    <th className="px-4 py-2">PO Number</th>
                    <th className="px-4 py-2">Supplier</th>
                    <th className="px-4 py-2">Item</th>
                    <th className="px-4 py-2 text-right">Qty</th>
                    <th className="px-4 py-2 text-right">Value</th>
                    <th className="px-4 py-2">Delivery Date</th>
                    <th className="px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.map((r) => (
                    <tr key={r.i} data-testid={`import-row-${r.i}`} className={`border-b last:border-0 ${r.errors.length ? "bg-red-50/60" : ""}`}>
                      <td className="px-4 py-2 font-mono text-muted-foreground">{r.i + 1}</td>
                      <td className="px-4 py-2 font-mono font-medium">{r.data.po_number || "—"}</td>
                      <td className="px-4 py-2">{r.data.supplier_name || "—"}</td>
                      <td className="max-w-44 truncate px-4 py-2 text-muted-foreground">{r.data.item || "—"}</td>
                      <td className="px-4 py-2 text-right font-mono">{r.data.quantity || "—"}</td>
                      <td className="px-4 py-2 text-right font-mono">{r.data.value || "—"}</td>
                      <td className="px-4 py-2 font-mono">{r.data.required_delivery_date || "—"}</td>
                      <td className="px-4 py-2">
                        {r.errors.length
                          ? <span className="flex items-center gap-1 text-[#DC2626]"><AlertCircle className="h-3 w-3" />{r.errors.join(", ")}</span>
                          : <span className="flex items-center gap-1 text-[#166534]"><CheckCircle2 className="h-3 w-3" />Ready</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" data-testid="import-back-btn" onClick={() => { setStep(1); setParsed(null); }}><ArrowLeft className="mr-1.5 h-4 w-4" /> Upload Different File</Button>
            <Button data-testid="import-confirm-btn" disabled={loading || validCount === 0} onClick={confirm} className="bg-slate-900 hover:bg-slate-800">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Import {validCount} POs <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {step === 3 && result && (
        <div className="rounded-lg border bg-card p-10 text-center" data-testid="import-result">
          <CheckCircle2 className="mx-auto h-12 w-12 text-[#22C55E]" />
          <h2 className="mt-4 font-heading text-xl font-semibold">Import complete</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            <span className="font-semibold text-[#166534]">{result.created} POs created</span>
            {result.skipped_duplicates > 0 && <> · {result.skipped_duplicates} duplicates skipped</>}
            {result.rejected > 0 && <> · <span className="text-[#DC2626]">{result.rejected} rows rejected</span></>}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Suppliers not found in the directory were created automatically.</p>
          <div className="mt-6 flex justify-center gap-2">
            <Button variant="outline" onClick={() => { setStep(1); setParsed(null); setResult(null); }}>Import More</Button>
            <Button data-testid="view-pos-btn" onClick={() => (window.location.href = "/buyer/pos")} className="bg-slate-900 hover:bg-slate-800">View Purchase Orders</Button>
          </div>
        </div>
      )}
    </div>
  );
}
